/**
 * The only module that reads `import.meta.env`.
 *
 * Everything configurable about this application is resolved here, once, so no component reaches for an
 * environment variable and a deployment has one place to look.
 */
export class Config {
	#authority: string;
	/** Where the Accounts service is reached */
	get authority() {
		return this.#authority;
	}

	#provider: string | undefined;
	/** The Supabase Auth project, for the browser side of the handshake */
	get provider() {
		return this.#provider;
	}

	#key: string | undefined;
	/** The project's publishable key. Not a secret: it is designed to be in a browser */
	get key() {
		return this.#key;
	}

	#legal: string | undefined;
	/**
	 * Where the terms and privacy policy are published. Absent until they are, and then no link to
	 * them is shown: a link to a page that does not exist yet is worse than none.
	 */
	get legal() {
		return this.#legal;
	}

	#people: string | undefined;
	/**
	 * The local people helper of an explicitly enabled local development composition, which lists local
	 * people and answers a provider token for one of them to this interface. Only a loopback address, or a
	 * path of this interface's own origin that its development server carries to a loopback helper
	 * (`/dev`, for a browser that cannot reach this computer's loopback, as through Live previews), is ever
	 * used; a real installation leaves it unset and no switcher exists.
	 */
	get people() {
		return this.#people;
	}

	constructor(values: Record<string, string | undefined> = import.meta.env as unknown as Record<string, string | undefined>) {
		this.#authority = (values.VITE_ACCOUNTS_AUTHORITY ?? 'http://127.0.0.1:1141').replace(/\/$/, '');
		this.#provider = values.VITE_ACCOUNTS_PROVIDER?.replace(/\/$/, '');
		this.#key = values.VITE_ACCOUNTS_PROVIDER_KEY;
		this.#legal = values.VITE_ACCOUNTS_LEGAL?.replace(/\/$/, '') || undefined;
		this.#people = Config.helper(values.VITE_ACCOUNTS_LOCAL_PEOPLE);
	}

	/**
	 * Where the local people helper is reached: a loopback origin, or an absolute path of this interface's own
	 * origin (lower-case segments only, so nothing but this origin can be named), which only its development
	 * server answers (`ACCOUNTS_PEOPLE_PROXY` in `vite.config.ts`); anything else is refused
	 */
	static helper(value: string | undefined): string | undefined {
		if (value && /^(\/[a-z][a-z0-9-]*)+\/?$/.test(value)) return value.replace(/\/$/, '');
		return Config.loopback(value);
	}

	/** A loopback origin, or nothing: anything else is refused as a local development helper */
	static loopback(value: string | undefined): string | undefined {
		if (!value) return undefined;
		try {
			const url = new URL(value);
			return url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) ? url.origin : undefined;
		} catch {
			return undefined;
		}
	}
}

export const config = new Config();

/**
 * Whether this build includes the quick login of local development (`/dev/quicklogin`, `/switch`). It is
 * decided when the interface is built, from `VITE_ACCOUNTS_LOCAL_PEOPLE`, so a build without the helper
 * carries none of that code; `config.people` still refuses a helper other than a loopback address or a
 * path of this origin.
 */
export const local: boolean = __ACCOUNTS_LOCAL__;
